import {
  EyeIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  Square2StackIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import React, { useState } from "react";

import { colors } from "../../colors";
import { Badge } from "../badge";
import { Button } from "../button";
import { Card } from "../card";
import { Flex } from "../flex";
import { FlexItem } from "../flex-item";
import { Input } from "../input";
import { LoadingSpinner } from "../loading-spinner";
import { Modal } from "../modal";
import { Pagination } from "../pagination";
import {
  ModifiedDate,
  StyledPostItem,
  StyledPostList,
  Title,
  TitleLink,
} from "./styled";
import type { PostItemProps, PostListProps } from "./types";

/**
 * Post Item Component
 *
 * Displays a single post item in the list
 *
 * @since 1.0.0
 */
export const PostItem: React.FC<PostItemProps> = ({
  post,
  statusLabels,
  modifiedPrefix,
  actions = { edit: true, delete: true, duplicate: true, preview: true },
  deleteModalTitle,
  deleteModalMessage,
  deleteConfirmButton,
  cancelButton,
  modalStrings,
  onEdit,
  onDelete,
  onDuplicate,
  onPreview,
  dateFormatter,
  className,
  testId,
  styleProps,
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleEdit = (): void => {
    if (onEdit) {
      onEdit(post.id);
    }
  };

  const handleDelete = (): void => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = (): void => {
    if (onDelete) {
      onDelete(post.id);
    }
    setIsDeleteModalOpen(false);
  };

  const handleCancelDelete = (): void => {
    setIsDeleteModalOpen(false);
  };

  const handleDuplicate = (): void => {
    if (onDuplicate) {
      onDuplicate(post.id);
    }
  };

  const handlePreview = (): void => {
    if (onPreview) {
      onPreview(post.id);
    }
  };

  const formatDate = (dateString: string): string => {
    if (dateFormatter) {
      return dateFormatter(dateString);
    }

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case "publish":
        return colors.success;
      case "draft":
        return colors.gray[400];
      case "pending":
        return colors.cyan[600];
      case "private":
        return colors.primary[300];
      case "future":
        return colors.gray[700];
      default:
        return colors.gray[500];
    }
  };

  const getStatusLabel = (status: string): string => {
    const statusKey = status.toLowerCase() as keyof typeof statusLabels;
    return (
      statusLabels[statusKey] ||
      status.charAt(0).toUpperCase() + status.slice(1)
    );
  };

  const buttonStyleProps = {
    backgroundColor: "transparent",
    color: colors.gray[800],
    borderWidth: "0",
    borderRadius: "5px",
    width: "32px",
    height: "32px",
    padding: "0",
    hoverBoxShadow: `0px 4px 10px ${colors.primary[100]}`,
    hoverBackgroundColor: "transparent",
    hoverColor: colors.primary[300],
  };

  return (
    <StyledPostItem $styleProps={styleProps}>
      <Card
        styleProps={{
          padding: styleProps?.padding || "13px 20px",
          border: `1px solid ${styleProps?.borderColor || colors.gray[100]}`,
          radius: styleProps?.borderRadius || "5px",
        }}
        {...(className && { className })}
        {...(testId && { testId })}
      >
        <Flex justify="space-between" align="center">
          <FlexItem>
            <Title $styleProps={styleProps}>
              <TitleLink onClick={handleEdit}>{post.title.rendered}</TitleLink>
              <Badge
                variant="solid"
                size="small"
                styleProps={{
                  backgroundColor: getStatusColor(post.status),
                  margin: "0 0 0 10px",
                }}
              >
                {getStatusLabel(post.status)}
              </Badge>
            </Title>
            <ModifiedDate $styleProps={styleProps}>
              {modifiedPrefix} {formatDate(post.modified)}
            </ModifiedDate>
          </FlexItem>
          <FlexItem>
            <Flex gap="5px">
              {actions.edit !== false && onEdit && (
                <Button
                  onClick={handleEdit}
                  styleProps={buttonStyleProps}
                  testId="post-item-edit"
                >
                  <PencilIcon width={16} height={16} />
                </Button>
              )}
              {actions.duplicate !== false && onDuplicate && (
                <Button
                  onClick={handleDuplicate}
                  styleProps={buttonStyleProps}
                  testId="post-item-duplicate"
                >
                  <Square2StackIcon width={16} height={16} />
                </Button>
              )}
              {actions.preview !== false && onPreview && (
                <Button
                  onClick={handlePreview}
                  styleProps={buttonStyleProps}
                  testId="post-item-preview"
                >
                  <EyeIcon width={16} height={16} />
                </Button>
              )}
              {actions.delete !== false && onDelete && (
                <Button
                  onClick={handleDelete}
                  styleProps={buttonStyleProps}
                  testId="post-item-delete"
                >
                  <TrashIcon width={16} height={16} />
                </Button>
              )}
            </Flex>
          </FlexItem>
        </Flex>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        title={deleteModalTitle}
        strings={modalStrings}
        footer={
          <Flex gap="10px" justify="flex-end">
            <Button variant="outline" onClick={handleCancelDelete}>
              {cancelButton}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              styleProps={{
                backgroundColor: colors.error,
                hoverBackgroundColor: "#c53030",
              }}
            >
              {deleteConfirmButton}
            </Button>
          </Flex>
        }
      >
        <p>{deleteModalMessage}</p>
      </Modal>
    </StyledPostItem>
  );
};

/**
 * Post List Component
 *
 * Displays a list of posts with search and filtering functionality
 *
 * @since 1.0.0
 */
export const PostList: React.FC<PostListProps> = ({
  posts,
  strings,
  loading = false,
  searchTerm = "",
  actions = { edit: true, delete: true, duplicate: true, preview: true },
  pagination,
  onSearch,
  onAddNew,
  onEdit,
  onDelete,
  onDuplicate,
  onPreview,
  onPageChange,
  onItemsPerPageChange,
  showItemsPerPageSelector = true,
  itemsPerPageOptions = [10, 20, 50, 100],
  maxPageButtons = 7,
  showPageInfo = true,
  showFirstLastButtons = true,
  dateFormatter,
  className,
  testId,
  styleProps,
}) => {
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (onSearch) {
      onSearch(e.target.value);
    }
  };

  const handleClearSearch = (): void => {
    if (onSearch) {
      onSearch("");
    }
  };

  if (loading && posts.length === 0) {
    return (
      <Card center styleProps={{ padding: "30px" }}>
        <LoadingSpinner />
      </Card>
    );
  }

  return (
    <Flex
      direction="column"
      gap="30px"
      {...(className && { className })}
      {...(testId && { testId })}
    >
      <FlexItem>
        <Flex justify="space-between" align="center">
          <FlexItem>
            <Input
              type="text"
              placeholder={strings.searchPlaceholder}
              value={searchTerm}
              variant="outline"
              startIcon={<MagnifyingGlassIcon width={20} height={20} />}
              onChange={handleSearch}
              testId="post-list-search"
            />
          </FlexItem>
          {onAddNew && (
            <FlexItem>
              <Button onClick={onAddNew} testId="post-list-add-new">
                <PlusIcon width={20} height={20} />
                {strings.addNewButton}
              </Button>
            </FlexItem>
          )}
        </Flex>
      </FlexItem>

      <FlexItem>
        {posts.length === 0 && !loading ? (
          <Card
            center
            styleProps={{
              border: `1px dashed ${colors.gray[200]}`,
              padding: "30px",
              radius: "5px",
            }}
          >
            <Flex direction="column" gap="5px">
              <FlexItem>
                <span>{strings.noPostsFound}</span>
              </FlexItem>
              <FlexItem>
                {searchTerm ? (
                  <span>
                    {strings.noSearchResults}{" "}
                    <Button
                      onClick={handleClearSearch}
                      variant="link"
                      size="small"
                      testId="post-list-clear-search"
                    >
                      {strings.clearSearch}
                    </Button>
                    .
                  </span>
                ) : (
                  <span>
                    {onAddNew && (
                      <>
                        <Button
                          onClick={onAddNew}
                          variant="link"
                          size="small"
                          testId="post-list-create-first"
                        >
                          {strings.createFirstPost}
                        </Button>
                        .
                      </>
                    )}
                  </span>
                )}
              </FlexItem>
            </Flex>
          </Card>
        ) : (
          <StyledPostList $styleProps={styleProps}>
            {posts.map((post) => (
              <PostItem
                key={post.id}
                post={post}
                statusLabels={strings.statusLabels}
                modifiedPrefix={strings.modifiedPrefix}
                actions={actions}
                editLabel={strings.editLabel}
                deleteModalTitle={strings.deleteModalTitle}
                deleteModalMessage={strings.deleteModalMessage}
                deleteConfirmButton={strings.deleteConfirmButton}
                cancelButton={strings.cancelButton}
                modalStrings={strings.modalStrings}
                onEdit={onEdit}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                onPreview={onPreview}
                dateFormatter={dateFormatter}
              />
            ))}
          </StyledPostList>
        )}

        {loading && posts.length > 0 && (
          <Card center styleProps={{ padding: "20px 0 0" }}>
            <LoadingSpinner />
          </Card>
        )}
      </FlexItem>

      {pagination && pagination.totalPages > 1 && (
        <FlexItem>
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            itemsPerPage={pagination.itemsPerPage}
            strings={strings.pagination}
            onPageChange={onPageChange || (() => {})}
            onItemsPerPageChange={onItemsPerPageChange}
            showItemsPerPageSelector={showItemsPerPageSelector}
            itemsPerPageOptions={itemsPerPageOptions}
            maxPageButtons={maxPageButtons}
            disabled={loading}
            showPageInfo={showPageInfo}
            showFirstLastButtons={showFirstLastButtons}
          />
        </FlexItem>
      )}
    </Flex>
  );
};
